import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import BottomNav from "../components/BottomNav";
import AddressSheet from "../components/AddressSheet";
import { useAppState } from "../context/AppState";
import { useLocale } from "../context/Locale";
import { api } from "../lib/api";
import { money } from "../lib/orderMath";

export default function ExplorePage() {
  const { address, openAddress } = useAppState();
  const { t } = useLocale();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ restaurants: [], items: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const normalizedQuery = query.trim();

  useEffect(() => {
    if (!address || normalizedQuery.length < 3) {
      setResults({ restaurants: [], items: [] });
      setLoading(false);
      setError("");
      return undefined;
    }

    let active = true;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError("");
      api("/search-restaurants", {
        method: "POST",
        body: JSON.stringify({
          q: normalizedQuery,
          latitude: Number(address.lat),
          longitude: Number(address.lng),
          accuracy: Math.max(0, Number(address.accuracy || 0)),
        }),
      })
        .then((data) => {
          if (!active) return;
          setResults({
            restaurants: Array.isArray(data?.restaurants) ? data.restaurants : [],
            items: Array.isArray(data?.items) ? data.items : [],
          });
        })
        .catch((requestError) => {
          if (active) setError(requestError.message);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 320);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [address, normalizedQuery]);

  const totalResults = useMemo(
    () => results.restaurants.length + results.items.length,
    [results],
  );

  return (
    <main className="food-app">
      <AppHeader />
      <div className="food-app__content explore-page">
        <header className="explore-page__heading">
          <small>DELIVERY HUARAZ</small>
          <h1>{t("exploreTitle")}</h1>
          <p>{t("exploreHelp")}</p>
        </header>

        <label className="food-search explore-search">
          <span aria-hidden="true">⌕</span>
          <input
            autoFocus
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("foodSearch")}
          />
        </label>

        {!address && (
          <section className="location-empty">
            <span>⌖</span>
            <h2>{t("chooseLocation")}</h2>
            <p>{t("chooseLocationHelp")}</p>
            <button type="button" onClick={openAddress}>{t("chooseAddress")}</button>
          </section>
        )}

        {address && normalizedQuery.length < 3 && (
          <div className="explore-hint">
            <span aria-hidden="true">⌕</span>
            <b>{t("searchThreeChars")}</b>
          </div>
        )}

        {loading && <div className="explore-loading"><i /><span>{t("searching")}</span></div>}
        {!loading && error && <div className="food-message food-message--error"><b>{error}</b></div>}
        {!loading && !error && normalizedQuery.length >= 3 && totalResults === 0 && (
          <div className="food-message"><b>{t("noSearchResults")}</b><span>{t("anotherWord")}</span></div>
        )}

        {!loading && results.restaurants.length > 0 && (
          <section className="explore-results">
            <div className="explore-results__heading">
              <h2>{t("businesses")}</h2><span>{results.restaurants.length}</span>
            </div>
            <div className="explore-business-list">
              {results.restaurants.map((restaurant) => (
                <Link to={`/food/stores/${restaurant.slug}`} key={`restaurant-${restaurant.id}`}>
                  <img src={restaurant.image} alt="" loading="lazy" />
                  <div>
                    <h3>{restaurant.name}</h3>
                    <p>{restaurant.description || t("viewMenu")}</p>
                    <span>★ {restaurant.avgRating || restaurant.rating || "0"} · ◷ {restaurant.delivery_time || "20"} min</span>
                  </div>
                  <b aria-hidden="true">›</b>
                </Link>
              ))}
            </div>
          </section>
        )}

        {!loading && results.items.length > 0 && (
          <section className="explore-results">
            <div className="explore-results__heading">
              <h2>{t("products")}</h2><span>{results.items.length}</span>
            </div>
            <div className="explore-product-grid">
              {results.items.map((item) => (
                <Link to={`/food/stores/${item.restaurant?.slug}`} key={`item-${item.id}`}>
                  <img src={item.image || item.restaurant?.image} alt="" loading="lazy" />
                  <div>
                    <h3>{item.name}</h3>
                    <p>{item.restaurant?.name}</p>
                    <b>{money(item.price)}</b>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
      <BottomNav />
      <AddressSheet />
    </main>
  );
}
